import { CopyOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Input, Modal, Select, Typography, message } from 'antd';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import { useCreateUser } from '../../api/Onboarding';
import { UserRoleType } from '../../constants';

const { Text, Paragraph } = Typography;

const Credentials = styled.div`
  margin-top: 16px;
  padding: 12px;
  border-radius: 6px;
  background: #fafafa;
  font-family: monospace;
  word-break: break-all;
`;

const ROLE_OPTIONS = [
  { value: UserRoleType.ADMINISTRATOR, label: 'Administrator — full access, can manage users' },
  { value: UserRoleType.EDITOR, label: 'Editor — can upload and analyse surveys' },
  { value: UserRoleType.VIEWER, label: 'Viewer — read-only across all surveys' },
  { value: UserRoleType.CLIENT, label: 'Client — only surveys shared with them' },
];

function AddUserModal({ open, onClose, organizationId }) {
  const [form] = Form.useForm();
  const { mutate: createUser, isLoading } = useCreateUser();
  // Held only until the modal closes; the API never returns it again.
  const [created, setCreated] = useState(null);

  const handleClose = () => {
    setCreated(null);
    form.resetFields();
    onClose();
  };

  const onFinish = (values) => {
    createUser(
      { data: values, organizationId },
      {
        onSuccess: (response) => setCreated(response.data),
        onError: (err) => message.error(err.message || 'Could not create the user'),
      },
    );
  };

  const copyCredentials = () => {
    navigator.clipboard
      .writeText(`Email: ${created.email}\nTemporary password: ${created.temporary_password}`)
      .then(() => message.success('Credentials copied'))
      .catch(() => message.error('Could not copy to clipboard'));
  };

  if (created) {
    return (
      <Modal
        title="User created"
        open={open}
        onCancel={handleClose}
        footer={[
          <Button key="copy" icon={<CopyOutlined />} onClick={copyCredentials}>
            Copy
          </Button>,
          <Button key="done" type="primary" onClick={handleClose}>
            Done
          </Button>,
        ]}
      >
        <Alert
          type="warning"
          showIcon
          message="Save this password now"
          description="It is shown once and cannot be retrieved later. The user must change it when they first sign in."
        />
        <Credentials>
          <div>
            <Text type="secondary">Email: </Text>
            {created.email}
          </div>
          <div>
            <Text type="secondary">Temporary password: </Text>
            {created.temporary_password}
          </div>
        </Credentials>
      </Modal>
    );
  }

  return (
    <Modal
      title="Add user"
      open={open}
      onCancel={handleClose}
      okText="Create user"
      onOk={() => form.submit()}
      confirmLoading={isLoading}
      destroyOnClose
    >
      <Paragraph type="secondary">
        The new account gets a temporary password, shown once after you create it.
      </Paragraph>
      <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Please enter a name.' }]}
        >
          <Input placeholder="Jane Client" data-cy="add-user-name" />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Please enter an email.' },
            { type: 'email', message: 'Please enter a valid email address.' },
          ]}
        >
          <Input placeholder="jane@fathom.com" data-cy="add-user-email" />
        </Form.Item>
        <Form.Item
          name="role"
          label="User type"
          initialValue={UserRoleType.VIEWER}
          rules={[{ required: true, message: 'Please choose a user type.' }]}
        >
          <Select options={ROLE_OPTIONS} data-cy="add-user-role" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

AddUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  organizationId: PropTypes.number.isRequired,
};

export default AddUserModal;
